import React, { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatIndianPercent as formatPercent,
  formatCompactInrAxis as formatCompact,
  formatLitres,
} from "../../../utils/reportFormat";
/** Balanced palette — clear product colors, brand accent for total. */
const CHART_COLORS = {
  petrol: "#16A34A",
  diesel: "#2563EB",
  "non-fuel": "#D97706",
  total: "#5D38DB",
  default: "#64748B",
};

const resolveSeriesColor = (series) => {
  if (!series) {
    return CHART_COLORS.default;
  }
  if (series.type === "total") {
    return CHART_COLORS.total;
  }
  if (series.type === "non_fuel") {
    return CHART_COLORS["non-fuel"];
  }

  const name = String(series.fuel || series.label || "").toLowerCase();
  if (name.includes("diesel")) {
    return CHART_COLORS.diesel;
  }
  if (name.includes("petrol")) {
    return CHART_COLORS.petrol;
  }

  return CHART_COLORS.default;
};

const gradientId = (key) =>
  `owner-sales-${String(key).replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`;

const SalesTrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{label}</p>
      {rows.map((item) => (
        <div className="owner-chart-tooltip__row" key={item.dataKey}>
          <span
            className="owner-chart-tooltip__dot"
            style={{ backgroundColor: item.color }}
          />
          <span className="owner-chart-tooltip__label">{item.name}</span>
          <span className="owner-chart-tooltip__value">
            {formatCurrency(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const LegendContent = ({ payload }) => {
  if (!payload?.length) {
    return null;
  }

  return (
    <div className="owner-chart-legend">
      {payload.map((entry) => (
        <span className="owner-chart-legend__item" key={entry.value}>
          <span
            className="owner-chart-legend__swatch"
            style={{ backgroundColor: entry.color }}
          />
          {entry.value}
        </span>
      ))}
    </div>
  );
};

const OwnerSalesTrendChart = ({ data = [], series = [], loading = false }) => {
  const areaSeries = useMemo(
    () => series.filter((item) => item.type !== "total"),
    [series]
  );
  const totalSeries = useMemo(
    () => series.find((item) => item.type === "total"),
    [series]
  );

  const summary = useMemo(() => {
    let periodTotal = 0;
    let peakValue = 0;
    let peakLabel = "—";
    let activeDays = 0;

    data.forEach((point) => {
      const total = Number(point.Total);
      if (!Number.isFinite(total) || total <= 0) {
        return;
      }
      periodTotal += total;
      activeDays += 1;
      if (total > peakValue) {
        peakValue = total;
        peakLabel = point.name;
      }
    });

    const average = activeDays > 0 ? periodTotal / activeDays : 0;

    return { periodTotal, peakValue, peakLabel, average, activeDays };
  }, [data]);

  if (loading) {
    return (
      <div className="owner-chart-empty">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="owner-chart-empty">
        <i className="fa fa-bar-chart owner-chart-empty__icon" aria-hidden="true" />
        <p>No sales data for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="owner-sales-chart">
      <div className="owner-sales-kpi row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--brand">
            <span className="owner-sales-kpi__label">Period total</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary.periodTotal)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--blue">
            <span className="owner-sales-kpi__label">Daily average</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary.average)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--green">
            <span className="owner-sales-kpi__label">Best day</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary.peakValue)}
            </strong>
            <small className="owner-sales-kpi__hint">{summary.peakLabel}</small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--amber">
            <span className="owner-sales-kpi__label">Active days</span>
            <strong className="owner-sales-kpi__value">{summary.activeDays}</strong>
          </div>
        </div>
      </div>

      <div className="owner-chart-panel">
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={data}
            margin={{ top: 12, right: 12, left: 4, bottom: 8 }}
          >
            <defs>
              {areaSeries.map((item) => {
                const color = resolveSeriesColor(item);
                const id = gradientId(item.key);
                return (
                  <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.42} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                  </linearGradient>
                );
              })}
            </defs>

            <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
              dy={8}
            />
            <YAxis
              tickFormatter={formatCompact}
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              width={72}
            />
            <Tooltip content={<SalesTrendTooltip />} />
            <Legend content={<LegendContent />} />

            {areaSeries.map((item) => {
              const color = resolveSeriesColor(item);
              return (
                <Area
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.label}
                  stackId="sales"
                  stroke={color}
                  fill={`url(#${gradientId(item.key)})`}
                  strokeWidth={2}
                  connectNulls
                  activeDot={{ r: 4, strokeWidth: 2, fill: "#fff", stroke: color }}
                />
              );
            })}

            {totalSeries ? (
              <Line
                type="monotone"
                dataKey={totalSeries.key}
                name={totalSeries.label}
                stroke={CHART_COLORS.total}
                strokeWidth={2.5}
                dot={{ r: 3, fill: CHART_COLORS.total, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: CHART_COLORS.total, stroke: "#fff", strokeWidth: 2 }}
                connectNulls
              />
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OwnerSalesTrendChart;
